// src/services/users.ts
import { api } from "@/lib/apiClient";

export type ListUsersParams = {
  q?: string;
  limit?: number;
  offset?: number;
};

export type UserListItem = {
  id: string;
  person_id: string;
  name: string;
  person_type: "PF" | "PJ";
  document_number?: string | null;
  email: string;
  is_active: boolean;
  unit_id: string | null;
  unit_name?: string | null;
  employee_code?: string | null;
  admission_date?: string | null;
  job_position_id?: string | null;
  job_position_name?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type UserContactOut = {
  id: string;
  kind: "email" | "phone" | "whatsapp" | "other";
  value: string;
  is_primary: boolean;
  created_at?: string;
};

export type UserAddressOut = {
  id: string;
  label?: string | null;
  zip?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  is_primary: boolean;
  created_at?: string;
};

export type UserClientCompanyAccess = {
  id?: string;
  client_company_id: string;
  tenant_id?: string;
  client_company_name?: string | null;
  client_company_legal_name?: string | null;
  client_company_document_number?: string | null;
  client_company_slug?: string | null;
  client_company_is_active?: boolean;
  group_id: string;
  group_name?: string | null;
  unit_id?: string | null;
  unit_name?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  job_position_id?: string | null;
  job_position_name?: string | null;
  employee_code?: string | null;
  admission_date?: string | null;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

export type UserDetailOut = {
  user: {
    id: string;
    person_id: string;
    email: string;
    is_active: boolean;
    last_login_at?: string | null;
    created_at?: string;
    updated_at?: string;
    tenant_id?: string | null;
    unit_id?: string | null;
  };
  person: {
    id: string;
    person_type: "PF" | "PJ";
    name: string;
    legal_name?: string | null;
    document_number?: string | null;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
    photo?: {
      base64: string;
      mime_type: string;
    } | null;
    photo_file_name?: string | null;
    photo_size_bytes?: number | null;
  };
  contacts: UserContactOut[];
  addresses: UserAddressOut[];
  tenant?: {
    tenant_id: string;
    tenant_slug: string;
    tenant_name: string;
    tenant_legal_name?: string | null;
    tenant_document?: string | null;
    is_active: boolean;
  } | null;
  unit?: {
    unit_id: string;
    tenant_id: string;
    unit_name: string;
    unit_legal_name?: string | null;
    unit_document_number?: string | null;
    is_active: boolean;
  } | null;
  employment?: {
    id: string;
    user_id: string;
    tenant_id: string;
    employee_code?: string | null;
    admission_date?: string | null;
    job_position_id?: string | null;
    job_position_name?: string | null;
    department_id?: string | null;
    department_name?: string | null;
    created_at?: string;
    updated_at?: string;
  } | null;
  groups: Array<{
    id: string;
    name: string;
    description?: string | null;
  }>;
  group_ids: string[];
  client_company_accesses: UserClientCompanyAccess[];
  permissions: string[];
};

export type UserUpsertIn = {
  email: string;
  password?: string;
  is_active?: boolean;
  unit_id?: string | null;
  group_ids: string[];
  client_company_accesses?: Array<{
    client_company_id: string;
    group_id?: string | null;
    unit_id?: string | null;
    department_id?: string | null;
    job_position_id?: string | null;
    employee_code?: string | null;
    admission_date?: string | null;
    is_active: boolean;
  }>;
  person: {
    person_type: "PF" | "PJ";
    name: string;
    legal_name?: string | null;
    document_number?: string | null;
    photo?: {
      photo_base64?: string | null;
      photo_mime_type?: string | null;
      photo_file_name?: string | null;
    } | null;
    contacts: Array<{
      kind: "email" | "phone" | "whatsapp" | "other";
      value: string;
      is_primary: boolean;
    }>;
    addresses: Array<{
      label?: string | null;
      zip?: string | null;
      street?: string | null;
      number?: string | null;
      complement?: string | null;
      district?: string | null;
      city?: string | null;
      state?: string | null;
      country?: string | null;
      is_primary: boolean;
    }>;
  };
  employment?: {
    employee_code?: string | null;
    admission_date?: string | null;
    job_position_id?: string | null;
    department_id?: string | null;
  } | null;
};

export type ClientUserOptionItem = { id: string; name: string };
export type ClientUserOptions = {
  client_company_id: string;
  units: ClientUserOptionItem[];
  departments: ClientUserOptionItem[];
  job_positions: ClientUserOptionItem[];
};

export async function listUsers(params: ListUsersParams = {}) {
  const { q = "", limit = 50, offset = 0 } = params;

  return api.get<UserListItem[]>("/users", {
    params: { q, limit, offset },
  });
}

export async function getUserDetail(userId: string) {
  return api.get<UserDetailOut>(`/users/${userId}`);
}

export async function getClientUserOptions(clientCompanyId: string) {
  return api.get<ClientUserOptions>(`/users/client-options/${clientCompanyId}`);
}

export async function createUser(payload: UserUpsertIn) {
  return api.post<{ id: string }>("/users", payload);
}

export async function updateUser(userId: string, payload: UserUpsertIn) {
  return api.put<{ id: string }>(`/users/${userId}`, payload);
}

export async function setUserActive(userId: string, is_active: boolean) {
  return api.patch<{ id: string }>(`/users/${userId}`, { is_active });
}

export async function setUserPassword(userId: string, password: string) {
  return api.patch<{ id: string }>(`/users/${userId}/password`, { password });
}
